import { cn } from '@gozoki/ui/lib/utils'
import { type ReactNode, useEffect } from 'react'
import { CardDivider, EmptyState } from './DashboardCard'
import { ModalCloseButton } from './ModalCloseButton'

/**
 * Coque des modales à la DA « C.A 7 jours » (fond crème, pastille d'icône, titre Bricolage 22 vert,
 * croix carrée 40 px, filet sous l'en-tête) : celles de C.A 7 jours et de Stock restant.
 *
 * Rendue DANS le canvas mis à l'échelle, comme `CamerasModal` : le `Dialog` partagé porte sur
 * `document.body`, donc hors du `transform: scale()` du dashboard.
 */
export function KioskModal({
  icon,
  title,
  ariaLabel,
  onClose,
  className,
  children,
}: {
  icon: ReactNode
  title: string
  ariaLabel: string
  onClose: () => void
  className?: string
  children: ReactNode
}) {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(7,44,26,0.25)] p-[32px]"
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      onClick={onClose}
    >
      <div
        className={cn(
          'flex max-h-full max-w-full flex-col gap-[24px] overflow-hidden rounded-[24px] bg-[#F0EDE2] p-[32px] drop-shadow-[0px_8px_20px_rgba(0,0,0,0.15)]',
          className,
        )}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex shrink-0 flex-col gap-[24px]">
          <div className="flex items-center gap-[12px]">
            <span className="flex size-[40px] shrink-0 items-center justify-center rounded-[12px] bg-[#F9F8F1] text-[#4F6A47]">
              {icon}
            </span>
            <h2 className="min-w-0 flex-1 truncate font-brand text-[22px] leading-[26px] font-semibold text-[#4F6A47]">
              {title}
            </h2>
            <ModalCloseButton onClose={onClose} variant="square" />
          </div>
          <CardDivider />
        </div>

        {children}
      </div>
    </div>
  )
}

/**
 * Vague décorative crème des panneaux de la maquette « CA 7 jours », même tracé que la vague du
 * hero C.A (`hero-wave.svg`). `-z-10` sous un panneau `isolate` : peinte sur son fond, sous son
 * contenu.
 */
function PanelWave({ className }: { className: string }) {
  return (
    <svg
      viewBox="0 0 640 278"
      fill="none"
      aria-hidden="true"
      className={cn('pointer-events-none absolute -z-10', className)}
    >
      <path
        d="M33.0532 246.605C12.2958 198.35 -10.2542 116.316 65.6045 174.222C160.428 246.605 170.964 103.541 113.999 54.4622C57.0341 5.38292 284.351 -24.7707 260.999 110.462C237.647 245.695 179.499 246.605 309.999 263.462C425.484 278.38 278.445 89.7144 371.499 49.4623C464.553 9.21029 428.259 168.413 569.079 88.2616C681.735 24.1407 604.515 232.827 550.499 204.462"
        stroke="#F0EDE2"
        strokeWidth="44"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/** Panneau clair des modales (fond #F9F8F1, rayon 16), avec la vague décorative en option. */
export function ModalPanel({
  className,
  wave,
  children,
}: {
  className?: string
  /** `wide` : grande vague en haut à droite ; `corner` : amorce dans l'angle haut droit. */
  wave?: 'wide' | 'corner'
  children?: ReactNode
}) {
  return (
    <div
      className={cn(
        'relative isolate overflow-hidden rounded-[16px] border border-[#0000000F] bg-[#F9F8F1]',
        className,
      )}
    >
      {wave === 'wide' ? <PanelWave className="-top-[170px] right-[40px] w-[720px]" /> : null}
      {wave === 'corner' ? <PanelWave className="-top-[150px] -right-[330px] w-[520px]" /> : null}
      {children}
    </div>
  )
}

/** Indicateur d'en-tête des modales : libellé gris au-dessus d'une valeur Bricolage 22. */
export function ModalKpi({
  label,
  value,
  color,
  className,
}: {
  label: string
  value: string
  color: string
  className?: string
}) {
  return (
    <div className={cn('flex flex-col items-end gap-[6px]', className)}>
      <span className="font-sans text-[14px] leading-[16px] font-medium whitespace-nowrap text-[#6C7A6B]">
        {label}
      </span>
      <span
        className="font-brand text-[22px] leading-[26px] font-extrabold whitespace-nowrap"
        style={{ color }}
      >
        {value}
      </span>
    </div>
  )
}

/**
 * Corps d'une modale chargée à l'ouverture : les données, sinon un squelette pendant le chargement,
 * ou un message sur erreur plutôt qu'un chargement sans fin (kiosk sans opérateur ; la requête se
 * relance d'elle-même).
 */
export function ModalBody<T>({
  data,
  isError,
  errorLabel,
  errorHeight,
  className,
  skeleton,
  children,
}: {
  data: T | undefined
  isError: boolean
  errorLabel: string
  errorHeight?: number
  className?: string
  skeleton: ReactNode
  children: (data: T) => ReactNode
}) {
  if (data !== undefined) return children(data)
  if (!isError) return skeleton
  return (
    <div className={cn('flex', className)} style={{ height: errorHeight }}>
      <EmptyState>{errorLabel}</EmptyState>
    </div>
  )
}
